import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Alert, Snackbar } from '@mui/material';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import ProductionPage from './pages/ProductionPage';
import PressFormsPage from './pages/PressFormsPage';
import MachinesPage from './pages/MachinesPage';
import ProductsPage from './pages/ProductsPage';
import RawMaterialsPage from './pages/RawMaterialsPage';
import DyesPage from './pages/DyesPage';
import PackagingMaterialsPage from './pages/PackagingMaterialsPage';
import FilamentPage from './pages/FilamentPage';
import StockPage from './pages/StockPage';
import FinishedGoodsStockPage from './pages/FinishedGoodsStockPage';
import MaintenancePlanPage from './pages/MaintenancePlanPage';
import MaintenanceKanbanPage from './pages/MaintenanceKanbanPage';
import LoginPage from './pages/LoginPage';
import PurchasesPage from './pages/PurchasesPage';
import SuppliersPage from './pages/SuppliersPage';
import CustomerOrdersHubPage from './pages/CustomerOrdersHubPage';
import CustomersPage from './pages/CustomersPage';
import CustomerEditPage from './pages/CustomerEditPage';
import RolesPage from './pages/RolesPage';
import StaffPage from './pages/StaffPage';
import QualitySettingsPage from './pages/QualitySettingsPage';
import OrderReportingPeriodsPage from './pages/OrderReportingPeriodsPage';
import CrushingPage from './pages/CrushingPage';
import CrmPage from './pages/CrmPage';
import LearningPage from './pages/LearningPage';
import StockCrushingWastePage from './pages/StockCrushingWastePage';
import PackagingPage from './pages/PackagingPage';
import PackagingDailyNormPage from './pages/PackagingDailyNormPage';
import MixingLinePage from './pages/MixingLinePage';
import MaterialsPriceDirectoryPage from './pages/MaterialsPriceDirectoryPage';
import TasksAppPage from './pages/TasksAppPage';
import PurchaseApprovalsPage from './pages/PurchaseApprovalsPage';
import SupplierEditPage from './pages/SupplierEditPage';
import SupplierCatalogPage from './pages/SupplierCatalogPage';
import ConsumableMaterialPage from './pages/ConsumableMaterialPage';
import ExportPage from './pages/ExportPage';
import ImportPage from './pages/ImportPage';
import StockHubPage from './pages/StockHubPage';
import OperatorPageV2 from './pages/OperatorPageV2';
import PlannerV2Page from './pages/PlannerV2Page';
import ProductionWarehousePage from './pages/ProductionWarehousePage';
import SetupPage from './pages/SetupPage';
import QCHubPage from './pages/QCHubPage';
import QCStockPage from './pages/QCStockPage';
import AdditionalProcessesSettingsPage from './pages/AdditionalProcessesSettingsPage';
import WorkProcessesPage from './pages/WorkProcessesPage';
import ProductKitsPage from './pages/ProductKitsPage';
import LeadsPage from './pages/LeadsPage';
import ProjectsPage from './pages/ProjectsPage';
import RnDPage from './pages/RnDPage';
import OEEPage from './pages/OEEPage';
import CostingPage from './pages/CostingPage';
import FinancePage from './pages/FinancePage';
import ActivityLogPage from './pages/ActivityLogPage';
import NotificationRulesPage from './pages/NotificationRulesPage';
import ProcessLogPage from './pages/ProcessLogPage';
import BulkEditPage from './pages/BulkEditPage';
import ShiftFloorDashboardPage from './pages/ShiftFloorDashboardPage';
import LmsCoursesPage from './pages/lms/LmsCoursesPage';
import LmsCourseDetailPage from './pages/lms/LmsCourseDetailPage';
import LmsTestsPage from './pages/lms/LmsTestsPage';
import LmsTakeQuizPage from './pages/lms/LmsTakeQuizPage';
import LmsResultsPage from './pages/lms/LmsResultsPage';
import LmsMaterialsPage from './pages/lms/LmsMaterialsPage';
import LmsSyncPage from './pages/lms/LmsSyncPage';
import { tryEnableWebPush, unsubscribeWebPush } from './services/pushSubscription';
import { AUTH_TOKEN_KEY, USER_UPDATED_EVENT, authService, clearSession } from './services/api';
import { getFirstAccessiblePath } from './utils/firstAccessiblePath';
import { isPathAccessible } from './utils/permissions';
import { canAccessShiftFloor } from './utils/shiftFloorAccess';
import './styles/lms-courses.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

/** Старий /qc-kanban → хаб ВКЯ (вкладка Заявки), з збереженням highlight/order. */
function QCKanbanToHubRedirect() {
  const [sp] = useSearchParams();
  const next = new URLSearchParams(sp);
  next.set('tab', 'requests');
  const q = next.toString();
  return <Navigate to={q ? `/qc?${q}` : '/qc?tab=requests'} replace />;
}

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [permsUpdated, setPermsUpdated] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('currentUser');
    // Профіль без токена (старий вхід до появи сесій) — потрібен повторний вхід
    if (stored && localStorage.getItem(AUTH_TOKEN_KEY)) {
      try {
        setCurrentUser(JSON.parse(stored));
      } catch {
        clearSession();
      }
    } else if (stored) {
      clearSession();
    }
    setAuthChecked(true);
  }, []);

  // Адміністратор змінив права/роль → профіль оновлено у фоні (services/api.js): меню й доступи без перелогіну
  useEffect(() => {
    const onUserUpdated = (e) => {
      setCurrentUser(e.detail.user);
      if (e.detail.permsChanged) setPermsUpdated(true);
    };
    window.addEventListener(USER_UPDATED_EVENT, onUserUpdated);
    return () => window.removeEventListener(USER_UPDATED_EVENT, onUserUpdated);
  }, []);

  useEffect(() => {
    if (!currentUser?.id) return undefined;
    const t = setTimeout(() => {
      // Тиха спроба: без системного діалогу; увімкнути можна з дзвіночка.
      tryEnableWebPush(currentUser, { requestPermission: false }).catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
  }, [currentUser]);

  const handleLogout = () => {
    // Відписка від push іде з токеном, тож сесію завершуємо після неї
    const doLogout = () => {
      authService.logout().finally(() => setCurrentUser(null));
    };
    const timeout = setTimeout(doLogout, 1500);
    unsubscribeWebPush().finally(() => {
      clearTimeout(timeout);
      doLogout();
    });
  };

  const DefaultRedirect = () => {
    // Начальник зміни / адмін з доступом до цеху — домашній екран моніторингу
    if (canAccessShiftFloor(currentUser) && currentUser?.oee_role === 'shift_supervisor') {
      return <Navigate to="/shift-floor" replace />;
    }
    const firstPath = getFirstAccessiblePath(currentUser);
    if (firstPath === '/') return <Dashboard />;
    return <Navigate to={firstPath} replace />;
  };

  const ProtectedRoute = ({ element }) => {
    const loc = useLocation();
    if (!authChecked) return null;
    if (!currentUser) return <Navigate to="/login" replace />;
    if (!isPathAccessible(loc.pathname, currentUser)) {
      return <Navigate to={getFirstAccessiblePath(currentUser)} replace />;
    }
    return element;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route
            path="/login"
            element={
              !authChecked ? null : currentUser ? (
                <Navigate to="/" replace />
              ) : (
                <LoginPage onLogin={setCurrentUser} />
              )
            }
          />
          <Route
            path="/*"
            element={
              <Layout currentUser={currentUser} onLogout={handleLogout}>
                <Routes>
                  <Route path="/" element={<ProtectedRoute element={<DefaultRedirect />} />} />
                  <Route path="/tasks" element={<ProtectedRoute element={<TasksAppPage />} />} />
                  <Route
                    path="/purchase-approvals"
                    element={<ProtectedRoute element={<PurchaseApprovalsPage />} />}
                  />
                  <Route
                    path="/production"
                    element={<ProtectedRoute element={<ProductionPage mode="planner" currentUser={currentUser} />} />}
                  />
                  <Route
                    path="/production-operator"
                    element={<ProtectedRoute element={<ProductionPage mode="operator" currentUser={currentUser} />} />}
                  />
                  <Route
                    path="/production-operator-v2"
                    element={<ProtectedRoute element={<OperatorPageV2 currentUser={currentUser} />} />}
                  />
                  <Route
                    path="/planner-v2"
                    element={<ProtectedRoute element={<PlannerV2Page />} />}
                  />
                  <Route
                    path="/shift-floor"
                    element={<ProtectedRoute element={<ShiftFloorDashboardPage />} />}
                  />
                  <Route
                    path="/production-warehouse"
                    element={<ProtectedRoute element={<ProductionWarehousePage />} />}
                  />
                  <Route
                    path="/setup"
                    element={<ProtectedRoute element={<SetupPage />} />}
                  />
                  <Route
                    path="/production-drying"
                    element={
                      <ProtectedRoute
                        element={<ProductionPage mode="operator" view="drying" currentUser={currentUser} />}
                      />
                    }
                  />
                  <Route
                    path="/production-mixing"
                    element={<ProtectedRoute element={<MixingLinePage currentUser={currentUser} />} />}
                  />
                  {/* /production-packaging-qc removed — redirect to packaging */}
                  <Route
                    path="/production-packaging-qc"
                    element={<Navigate to="/packaging" replace />}
                  />
                  <Route
                    path="/qc"
                    element={<ProtectedRoute element={<QCHubPage currentUser={currentUser} />} />}
                  />
                  <Route
                    path="/qc-kanban"
                    element={
                      <ProtectedRoute
                        element={<QCKanbanToHubRedirect />}
                      />
                    }
                  />
                  <Route
                    path="/qc-stock-page"
                    element={<ProtectedRoute element={<QCStockPage />} />}
                  />
                  <Route
                    path="/settings-additional-processes"
                    element={<ProtectedRoute element={<AdditionalProcessesSettingsPage />} />}
                  />
                  <Route
                    path="/work-processes"
                    element={<ProtectedRoute element={<WorkProcessesPage />} />}
                  />
                  <Route
                    path="/maintenance-plan"
                    element={<ProtectedRoute element={<MaintenancePlanPage />} />}
                  />
                  <Route
                    path="/maintenance"
                    element={<ProtectedRoute element={<MaintenanceKanbanPage />} />}
                  />
                  <Route
                    path="/press-forms"
                    element={<ProtectedRoute element={<PressFormsPage />} />}
                  />
                  <Route
                    path="/machines"
                    element={<ProtectedRoute element={<MachinesPage />} />}
                  />
                  <Route
                    path="/products"
                    element={<ProtectedRoute element={<ProductsPage />} />}
                  />
                  <Route
                    path="/raw-materials"
                    element={<ProtectedRoute element={<RawMaterialsPage />} />}
                  />
                  <Route
                    path="/dyes"
                    element={<ProtectedRoute element={<DyesPage />} />}
                  />
                  <Route
                    path="/packaging-materials"
                    element={<ProtectedRoute element={<PackagingMaterialsPage />} />}
                  />
                  <Route
                    path="/filament"
                    element={<ProtectedRoute element={<FilamentPage />} />}
                  />
                  <Route
                    path="/stock"
                    element={<ProtectedRoute element={<StockHubPage currentUser={currentUser} />} />}
                  />
                  <Route path="/stock-raw" element={<Navigate to="/stock?tab=raw" replace />} />
                  <Route path="/stock-dyes" element={<Navigate to="/stock?tab=dyes" replace />} />
                  <Route path="/stock-packaging" element={<Navigate to="/stock?tab=packaging" replace />} />
                  <Route path="/stock-technical" element={<Navigate to="/stock?tab=technical" replace />} />
                  <Route path="/stock-semi" element={<Navigate to="/stock?tab=semi" replace />} />
                  <Route path="/stock-packaging-work" element={<Navigate to="/stock?tab=packaging-work" replace />} />
                  <Route path="/stock-crushing-waste" element={<Navigate to="/stock?tab=crushing-waste" replace />} />
                  <Route path="/stock-finished" element={<Navigate to="/stock?tab=finished" replace />} />
                  <Route path="/stock-blocking" element={<Navigate to="/stock?tab=blocking" replace />} />
                  <Route path="/stock-consumable" element={<Navigate to="/stock?tab=consumable" replace />} />
                  <Route path="/consumable-materials" element={<ProtectedRoute element={<ConsumableMaterialPage />} />} />
                  <Route
                    path="/packaging"
                    element={<ProtectedRoute element={<PackagingPage currentUser={currentUser} />} />}
                  />
                  <Route
                    path="/packaging-daily-norm"
                    element={<ProtectedRoute element={<PackagingDailyNormPage />} />}
                  />
                  <Route path="/materials-price-directory" element={<Navigate to="/stock" replace />} />
                  <Route path="/sample-warehouse" element={<Navigate to="/stock?tab=samples" replace />} />
                  <Route
                    path="/export"
                    element={<ProtectedRoute element={<ExportPage />} />}
                  />
                  <Route
                    path="/import"
                    element={<ProtectedRoute element={<ImportPage />} />}
                  />
                  <Route
                    path="/purchases"
                    element={<ProtectedRoute element={<PurchasesPage currentUser={currentUser} />} />}
                  />
                  <Route
                    path="/suppliers"
                    element={<ProtectedRoute element={<SuppliersPage />} />}
                  />
                  <Route path="/suppliers/:id" element={<ProtectedRoute element={<SupplierEditPage />} />} />
                  <Route path="/supplier-catalog" element={<ProtectedRoute element={<SupplierCatalogPage />} />} />
                  <Route
                    path="/customer-orders"
                    element={<ProtectedRoute element={<CustomerOrdersHubPage />} />}
                  />
                  <Route
                    path="/customers/:id"
                    element={<ProtectedRoute element={<CustomerEditPage />} />}
                  />
                  <Route
                    path="/customers"
                    element={<ProtectedRoute element={<CustomersPage />} />}
                  />
                  <Route
                    path="/settings-roles"
                    element={<ProtectedRoute element={<RolesPage />} />}
                  />
                  <Route
                    path="/settings-staff"
                    element={<ProtectedRoute element={<StaffPage />} />}
                  />
                  <Route
                    path="/settings-quality"
                    element={<ProtectedRoute element={<QualitySettingsPage />} />}
                  />
                  <Route
                    path="/settings-order-periods"
                    element={<ProtectedRoute element={<OrderReportingPeriodsPage />} />}
                  />
                  <Route
                    path="/crushing"
                    element={<ProtectedRoute element={<CrushingPage />} />}
                  />
                  <Route
                    path="/crm"
                    element={<ProtectedRoute element={<CrmPage />} />}
                  />
                  <Route path="/learning" element={<Navigate to="/learning/courses" replace />} />
                  <Route
                    path="/learning/:section"
                    element={<ProtectedRoute element={<LearningPage />} />}
                  />
                  <Route
                    path="/product-kits"
                    element={<ProtectedRoute element={<ProductKitsPage />} />}
                  />
                  <Route
                    path="/leads"
                    element={<ProtectedRoute element={<LeadsPage />} />}
                  />
                  <Route
                    path="/projects"
                    element={<ProtectedRoute element={<ProjectsPage />} />}
                  />
                  <Route
                    path="/rnd"
                    element={<ProtectedRoute element={<RnDPage currentUser={currentUser} />} />}
                  />
                  <Route path="/oee" element={<ProtectedRoute element={<OEEPage />} />} />
                  <Route path="/costing" element={<ProtectedRoute element={<CostingPage />} />} />
                  <Route path="/finance" element={<ProtectedRoute element={<FinancePage />} />} />
                  <Route
                    path="/activity-log"
                    element={<ProtectedRoute element={<ActivityLogPage currentUser={currentUser} />} />}
                  />
                  <Route
                    path="/notification-rules"
                    element={<ProtectedRoute element={<NotificationRulesPage />} />}
                  />
                  <Route
                    path="/process-log"
                    element={<ProtectedRoute element={<ProcessLogPage currentUser={currentUser} />} />}
                  />
                  <Route
                    path="/bulk-edit"
                    element={<ProtectedRoute element={<BulkEditPage />} />}
                  />
                  <Route
                    path="/lms/courses"
                    element={<ProtectedRoute element={<LmsCoursesPage />} />}
                  />
                  <Route
                    path="/lms/courses/:id"
                    element={<ProtectedRoute element={<LmsCourseDetailPage />} />}
                  />
                  <Route path="/lms/manage" element={<Navigate to="/lms/courses" replace />} />
                  <Route path="/lms/groups" element={<Navigate to="/lms/courses?tab=groups" replace />} />
                  <Route
                    path="/lms/tests"
                    element={<ProtectedRoute element={<LmsTestsPage />} />}
                  />
                  <Route
                    path="/lms/tests/:id"
                    element={<ProtectedRoute element={<LmsTakeQuizPage />} />}
                  />
                  <Route
                    path="/lms/results"
                    element={<ProtectedRoute element={<LmsResultsPage />} />}
                  />
                  <Route path="/lms/dashboard" element={<Navigate to="/lms/results" replace />} />
                  <Route path="/lms/manage-quizzes" element={<Navigate to="/lms/tests" replace />} />
                  <Route
                    path="/lms/materials"
                    element={<ProtectedRoute element={<LmsMaterialsPage />} />}
                  />
                  <Route
                    path="/lms/sync"
                    element={<ProtectedRoute element={<LmsSyncPage />} />}
                  />
                </Routes>
              </Layout>
            }
          />
        </Routes>
      </Router>
      <Snackbar
        open={permsUpdated}
        autoHideDuration={6000}
        onClose={() => setPermsUpdated(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" onClose={() => setPermsUpdated(false)}>
          Ваші права доступу оновлено — меню змінилось.
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default App;
